// @flow
import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { FormattedMessage } from 'mz-intl';
import { getSearchPayLater, getActiveLanguage } from 'app/sagas/selectors';
import messages from '../messages.intl';
import LockIcon from './LockIcon';
import { Container, IconCircle, Content, Title, Text, Strong } from './elements';

// types
type Props = {
  available: boolean,
  dueDate: ?string,
  locale: ?string
};

// Locale-aware "month day" without the year, so the ordering follows the active
// language (e.g. "September 15" vs "15 de setembro"). Build the date in UTC from
// the parseZone'd calendar day so the timezone can't shift it, and normalize the
// app locale (e.g. "pt_BR") to a BCP-47 tag that Intl accepts.
const formatDueDate = (dueDate: string, locale: ?string): ?string => {
  const parsed = moment.parseZone(dueDate);
  if (!parsed.isValid()) return null;

  const utcDay = new Date(Date.UTC(parsed.year(), parsed.month(), parsed.date()));
  try {
    return new Intl.DateTimeFormat((locale || 'en').replace(/_/g, '-'), {
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    }).format(utcDay);
  } catch (e) {
    return parsed.format('MMMM D');
  }
};

export const BuyNowPayLaterBanner = ({ available, dueDate, locale }: Props) => {
  if (!available || !dueDate) return null;

  const formattedDueDate = formatDueDate(dueDate, locale);
  if (!formattedDueDate) return null;

  return (
    <Container data-testid="bnpl-banner">
      <IconCircle>
        <LockIcon />
      </IconCircle>
      <Content>
        <Title>
          <FormattedMessage message={messages.BNPL_BANNER_TITLE} />
        </Title>
        <Text>
          <FormattedMessage
            message={messages.BNPL_BANNER_TEXT}
            values={{ date: <Strong>{formattedDueDate}</Strong> }}
          />
        </Text>
      </Content>
    </Container>
  );
};

const mapStateToProps = (state) => {
  const payLater = getSearchPayLater(state);

  return {
    available: !!payLater?.available,
    dueDate: payLater?.dueDate || null,
    locale: getActiveLanguage(state)
  };
};

export default connect(mapStateToProps)(BuyNowPayLaterBanner);
